// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  authorize,
  exportAudience,
  filenameFromDisposition,
  getSegments,
  listAuthorizations,
  listExports,
  readBlobError,
} from '../../api/audiencesApi';
import { getActiveTenant } from '../../helpers/tenantHelpers';
import './Audiences.css';

/* Marketing → Audiences — "Pick segment, pick network, authorize, export."
   Contract: docs/WEB_VISIBILITY_CONTRACTS_2026-09-22.md §5 (crm_backend, app
   `audiences`). One pipeline, three outputs: the segment is the campaign
   builder's selector, rows are normalized + SHA-256 on the server (no raw
   export path exists), the file/push is per network. Nothing leaves without
   a recorded owner authorization naming segment, destination and count; every
   export is ledgered, and the last export per network is shown up top so
   nobody re-uploads a stale list. On 404 the page says the backend isn't
   deployed — never sample data. */

const NETWORKS = [
  { key: 'google', label: 'Google Ads', product: 'Customer Match' },
  { key: 'meta', label: 'Meta Ads', product: 'Custom Audiences' },
  { key: 'openai', label: 'ChatGPT Ads', product: 'customer list' },
];

const PRESETS = [
  { key: 'active_customers', label: 'Active customers', hint: 'Exclude from acquisition ads — every channel' },
  { key: 'inactive_customers', label: 'Inactive customers', hint: 'Win-back target' },
  { key: 'frozen_with_payment', label: 'Frozen plans with payment on file', hint: 'Win-back target — payment already on file' },
];

const EXCLUSION_LABELS = {
  opted_out: 'opted out',
  no_contact_info: 'no contact info',
  suppressed: 'suppressed',
  unknown_import: 'unknown import',
  recently_contacted: 'recently contacted',
  scanner_completed: 'scanner completed',
};

const STALE_DAYS = 30;
const num = (v) => (v == null || v === '' ? '—' : Number(v).toLocaleString());
const fmtTime = (v) => { if (!v) return ''; try { return new Date(v).toLocaleString(); } catch { return String(v); } };
const daysSince = (v) => { if (!v) return null; const ms = Date.now() - new Date(v).getTime(); return Number.isFinite(ms) ? Math.floor(ms / 86400000) : null; };
const personName = (x) => {
  if (!x) return '';
  if (typeof x === 'string') return x;
  return [x.first_name, x.last_name].filter(Boolean).join(' ').trim() || x.name || x.full_name || x.email || (x.id != null ? `#${x.id}` : '');
};
const segmentLabel = (key, segments) => segments.find((s) => s.key === key)?.label || PRESETS.find((p) => p.key === key)?.label || key || '—';

function LastExportCard({ network, last, segments }) {
  const age = daysSince(last?.created_at);
  const stale = !last || (age != null && age > STALE_DAYS);
  return (
    <div className={`au-last ${stale ? 'is-stale' : ''}`}>
      <div className="au-last-net">{network.label}<span className="au-dim">{network.product}</span></div>
      {last ? (
        <>
          <div className="au-last-count">{num(last.row_count)}<span className="au-dim"> rows</span></div>
          <div className="au-last-meta">
            {segmentLabel(last.segment_key, segments)} · {last.mode === 'push' ? 'pushed' : 'file'} · {fmtTime(last.created_at)}
            {last.created_by ? ` · by ${personName(last.created_by)}` : ''}
          </div>
          {stale && <div className="au-stale">{age} days old — the list on {network.label} is probably stale; re-export before relying on it.</div>}
          {last.format_verified === false && <div className="au-dim au-small">Column header not yet confirmed in the ad manager.</div>}
        </>
      ) : (
        <>
          <div className="au-last-count au-dim">never</div>
          <div className="au-stale">Nothing has been exported to {network.label} from this workspace.</div>
        </>
      )}
    </div>
  );
}

export default function Audiences() {
  const tenant = getActiveTenant() || {};
  const [segments, setSegments] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [authorizations, setAuthorizations] = useState([]);
  const [exports, setExports] = useState([]);
  const [lastByNetwork, setLastByNetwork] = useState({});
  const [loading, setLoading] = useState(true);
  const [notDeployed, setNotDeployed] = useState(false);
  const [error, setError] = useState(null);

  const [segmentKey, setSegmentKey] = useState('');
  const [networkKey, setNetworkKey] = useState('');
  const [attestation, setAttestation] = useState('');
  const [busy, setBusy] = useState('');
  const [actionError, setActionError] = useState(null);
  const [actionNote, setActionNote] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setNotDeployed(false);
    try {
      const [seg, auths, exps] = await Promise.all([getSegments(), listAuthorizations(), listExports()]);
      const s = seg.data || {};
      setSegments(Array.isArray(s.segments) ? s.segments : []);
      setNetworks(Array.isArray(s.networks) ? s.networks : []);
      setPolicy(s.policy || null);
      setAuthorizations(Array.isArray(auths.data?.authorizations) ? auths.data.authorizations : Array.isArray(auths.data) ? auths.data : []);
      const e = exps.data || {};
      setExports(Array.isArray(e.exports) ? e.exports : Array.isArray(e) ? e : []);
      setLastByNetwork(e.last_by_network || {});
    } catch (err) {
      if (err?.status === 404) setNotDeployed(true);
      else setError(err?.message || 'Couldn’t load audiences.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Presets come from the API (live counts); the static list only orders/labels them.
  const presetRows = useMemo(() => {
    const byKey = new Map(segments.map((s) => [s.key, s]));
    const ordered = PRESETS.map((p) => ({ ...p, ...(byKey.get(p.key) || {}), known: byKey.has(p.key) }));
    const extra = segments.filter((s) => !PRESETS.some((p) => p.key === s.key)).map((s) => ({ ...s, known: true }));
    return [...ordered, ...extra];
  }, [segments]);

  const network = useMemo(() => {
    const api = networks.find((n) => n.key === networkKey) || {};
    const base = NETWORKS.find((n) => n.key === networkKey) || {};
    return networkKey ? { ...base, ...api } : null;
  }, [networks, networkKey]);

  const currentAuth = useMemo(() => {
    if (!segmentKey || !networkKey) return null;
    const live = authorizations.filter((a) => !a.revoked_at && (!a.expires_at || new Date(a.expires_at) > new Date()));
    return live.find((a) => (a.segment_key || a.segment_filter?.segment_key) === segmentKey && String(a.network || a.note || '').toLowerCase().includes(networkKey)) || null;
  }, [authorizations, segmentKey, networkKey]);

  const selectedSegment = presetRows.find((p) => p.key === segmentKey) || null;

  const onAuthorize = async () => {
    setBusy('authorize'); setActionError(null); setActionNote(null);
    try {
      const { data } = await authorize({ segment_key: segmentKey, network: networkKey, attestation: attestation.trim() });
      setActionNote(`Authorization recorded${data?.segment_count_at_grant != null ? ` for ${num(data.segment_count_at_grant)} rows` : ''}.`);
      setAttestation('');
      await load();
    } catch (err) {
      setActionError(err?.message || 'authorization_failed');
    } finally { setBusy(''); }
  };

  const onExport = async (mode) => {
    setBusy(mode); setActionError(null); setActionNote(null);
    try {
      const res = await exportAudience({ segment_key: segmentKey, network: networkKey, mode });
      if (mode === 'file') {
        const name = filenameFromDisposition(res.headers || {}, `${tenant.slug || 'audience'}-${segmentKey}-${networkKey}.csv`);
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        setActionNote(`Downloaded ${name}.`);
      } else {
        const r = res.data || {};
        setActionNote(r.detail || r.status || `Pushed to ${network?.label || networkKey}.`);
      }
      await load();
    } catch (err) {
      setActionError(mode === 'file' ? await readBlobError(err) : (err?.message || 'export_failed'));
    } finally { setBusy(''); }
  };

  const pushReason = network ? (network.connected && network.push_available !== false ? '' : (network.reason || (network.connected ? 'not_available' : 'channel not connected'))) : '';

  return (
    <div className="au">
      <div className="au-head">
        <div>
          <div className="au-eyebrow">Marketing</div>
          <h1 className="au-title">Audiences</h1>
          <div className="au-sub">{tenant.name || 'Your workspace'} · pick a segment, pick a network, authorize, export. Rows are hashed (SHA-256) on the server; no raw list ever leaves.</div>
        </div>
      </div>

      {loading && !segments.length && !notDeployed && <div className="au-muted">Loading…</div>}
      {notDeployed && (
        <div className="au-note warn"><strong>Audience export isn’t deployed on the backend yet.</strong> This page is wired to <code>/api/marketing/audiences/</code> and populates on the next backend deploy — nothing here is sample data.</div>
      )}
      {error && <div className="au-note danger">{error} <button type="button" className="au-link" onClick={load}>Retry</button></div>}

      {!notDeployed && !error && (segments.length > 0 || exports.length > 0 || !loading) && (
        <>
          <div className="au-card">
            <div className="au-card-title">Last export per network<span className="au-dim">so nobody re-uploads a stale list</span></div>
            <div className="au-last-grid">
              {NETWORKS.map((n) => <LastExportCard key={n.key} network={n} last={lastByNetwork[n.key] || null} segments={segments} />)}
            </div>
          </div>

          <div className="au-steps">
            {/* 1 — segment */}
            <div className="au-card">
              <div className="au-card-title"><span><span className="au-step">1</span>Segment</span><span className="au-dim">same selector as the campaign builder</span></div>
              <div className="au-segments" role="radiogroup" aria-label="Segment">
                {presetRows.map((p) => (
                  <label key={p.key} className={`au-seg ${segmentKey === p.key ? 'is-on' : ''} ${p.known ? '' : 'is-unknown'}`}>
                    <input type="radio" name="segment" value={p.key} checked={segmentKey === p.key} onChange={() => setSegmentKey(p.key)} disabled={!p.known} />
                    <span className="au-seg-body">
                      <span className="au-seg-name">{p.label}</span>
                      <span className="au-seg-hint">{p.definition || p.hint}</span>
                      <span className="au-seg-count">
                        {p.known ? <><b>{num(p.sendable)}</b> exportable</> : <span className="au-dim">not offered by the backend</span>}
                        {p.exclusions && Object.values(p.exclusions).some((n) => Number(n) > 0) && (
                          <span className="au-excl"> · excluded: {Object.entries(p.exclusions).filter(([, n]) => Number(n) > 0).map(([k, n]) => `${num(n)} ${EXCLUSION_LABELS[k] || k.replace(/_/g, ' ')}`).join(', ')}</span>
                        )}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              <div className="au-muted au-small">Custom segments (any campaign-builder filter) are accepted by the API; this screen ships the presets — the campaign builder has no reusable filter component yet.</div>
            </div>

            {/* 2 — network */}
            <div className="au-card">
              <div className="au-card-title"><span><span className="au-step">2</span>Network</span></div>
              <div className="au-nets" role="radiogroup" aria-label="Network">
                {NETWORKS.map((n) => {
                  const api = networks.find((x) => x.key === n.key) || {};
                  return (
                    <label key={n.key} className={`au-net ${networkKey === n.key ? 'is-on' : ''}`}>
                      <input type="radio" name="network" value={n.key} checked={networkKey === n.key} onChange={() => setNetworkKey(n.key)} />
                      <span className="au-seg-body">
                        <span className="au-seg-name">{n.label}</span>
                        <span className="au-seg-hint">{n.product}{api.format ? ` · ${api.format}` : ''}</span>
                        <span className={`au-conn ${api.connected ? 'is-on' : ''}`}>{api.connected ? 'connected — push available' : 'file download only'}{!api.connected && api.reason ? ` (${api.reason})` : ''}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* 3 — authorize */}
            <div className="au-card">
              <div className="au-card-title"><span><span className="au-step">3</span>Authorize</span><span className="au-dim">recorded owner sign-off — segment, destination, count</span></div>
              {!segmentKey || !networkKey ? (
                <div className="au-muted">Pick a segment and a network first.</div>
              ) : currentAuth ? (
                <div className="au-auth-ok">
                  <div><b>Authorized</b> by {personName(currentAuth.authorized_by) || 'the owner'}{currentAuth.authorized_role ? ` (${currentAuth.authorized_role})` : ''} on {fmtTime(currentAuth.authorized_at)}</div>
                  <div className="au-dim">{num(currentAuth.segment_count_at_grant)} rows at grant · expires {fmtTime(currentAuth.expires_at)}{currentAuth.policy_attestation ? ' · privacy policy attested' : ''}</div>
                </div>
              ) : (
                <div className="au-auth-form">
                  <p className="au-policy">Your privacy policy must disclose advertising audiences before this export can run.</p>
                  {policy?.text && <p className={`au-policy-status ${policy.status || ''}`}>{policy.text}</p>}
                  <label className="au-field">
                    <span>Attestation — confirm, in your own words, that {tenant.name || 'this business'}’s privacy policy permits sharing hashed customer identifiers with {network?.label || 'this network'} for advertising suppression or targeting</span>
                    <textarea rows={3} value={attestation} onChange={(e) => setAttestation(e.target.value)} placeholder="e.g. Our privacy policy (section 6, updated today) discloses hashed uploads to Google, Meta and OpenAI for advertising audiences." />
                  </label>
                  <div className="au-actions">
                    <button type="button" className="au-btn" disabled={busy !== '' || attestation.trim().length < 20} onClick={onAuthorize}>{busy === 'authorize' ? 'Recording…' : `Authorize ${selectedSegment?.label || segmentKey} → ${network?.label || networkKey}`}</button>
                    <span className="au-dim au-small">Recorded with your name, role, the segment and today’s count.</span>
                  </div>
                </div>
              )}
            </div>

            {/* 4 — export */}
            <div className="au-card">
              <div className="au-card-title"><span><span className="au-step">4</span>Export</span><span className="au-dim">hashed rows only</span></div>
              {!segmentKey || !networkKey ? (
                <div className="au-muted">Pick a segment and a network first.</div>
              ) : (
                <div className="au-actions">
                  <button type="button" className="btn-primary au-btn-primary" disabled={busy !== ''} onClick={() => onExport('file')}>{busy === 'file' ? 'Building…' : 'Download file'}</button>
                  <button type="button" className="au-btn" disabled={busy !== '' || !!pushReason} title={pushReason || undefined} onClick={() => onExport('push')}>{busy === 'push' ? 'Pushing…' : `Push to ${network?.label || networkKey}`}</button>
                  {pushReason && <span className="au-dim au-small">Push unavailable: {pushReason}</span>}
                  {!currentAuth && <span className="au-dim au-small">The backend refuses without an authorization (step 3).</span>}
                </div>
              )}
              {actionError && <div className="au-note danger" role="alert">{String(actionError)}</div>}
              {actionNote && <div className="au-note ok" role="status">{actionNote}</div>}
            </div>
          </div>

          <div className="au-card">
            <div className="au-card-title">History<span className="au-dim">{exports.length ? `${exports.length} export${exports.length === 1 ? '' : 's'}` : 'no exports yet'}</span></div>
            {exports.length > 0 && (
              <div className="au-table-wrap">
                <table className="au-table">
                  <thead><tr><th>When</th><th>Segment</th><th>Network</th><th>Mode</th><th className="num">Rows</th><th>By</th><th>File digest</th><th>Result</th></tr></thead>
                  <tbody>
                    {exports.map((e) => (
                      <tr key={e.id || `${e.created_at}-${e.network}`}>
                        <td>{fmtTime(e.created_at)}</td>
                        <td>{segmentLabel(e.segment_key, segments)}</td>
                        <td>{NETWORKS.find((n) => n.key === e.network)?.label || e.network}</td>
                        <td>{e.mode}</td>
                        <td className="num">{num(e.row_count)}</td>
                        <td>{personName(e.created_by) || '—'}</td>
                        <td className="au-mono" title={e.sha256 || ''}>{e.sha256 ? `${e.sha256.slice(0, 12)}…` : '—'}</td>
                        <td>{e.push_result?.detail || e.push_result?.status || (e.mode === 'file' ? e.filename || 'downloaded' : '—')}{e.format_verified === false ? <span className="au-dim"> · header unverified</span> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
